import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Languages, RotateCcw, EyeOff, Star } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { api, errMsg } from "../../lib/api";
import { useFetch } from "../../lib/useFetch";
import { FormGrid } from "./FormField";
import { TAX_OPTIONS } from "./adminConfigs";
import { AdminH } from "./AdminLayout";

const STATUSES = ["Draft", "Registrations open", "Registrations closed", "Completed", "Cancelled"];
const SECTION_LABELS = { level: "Level", how_to_register: "How to register and pay", training_program: "Training program", accommodation: "Accommodation", food: "Food", parents: "Parents" };

const GROUPS = [
  ["General information", [
    { key: "title", label: "Title", full: true },
    { key: "class_names", label: "Sailing classes (one or more)", type: "multi", options: { res: "classes", value: "name" }, full: true },
    { key: "whatsapp_link", label: "WhatsApp link (event group)", full: true },
    { key: "start_date", label: "Start date", type: "date" }, { key: "end_date", label: "End date", type: "date" }, { key: "reg_deadline", label: "Registration deadline", type: "date" },
    { key: "image", label: "Cover image", type: "image", full: true }, { key: "featured", label: "Featured on home page", type: "bool" },
    { key: "description", label: "Description", type: "textarea", rows: 4, full: true }, { key: "confirmation_info", label: "Information for the confirmation email (added after the cost total in the enrolment summary email, in English)", type: "textarea", rows: 4, full: true }]],
  ["Organization", [
    { key: "location_id", label: "Location", type: "select", options: { res: "locations" } }, { key: "head_coach_id", label: "Head Coach", type: "select", options: { res: "instructors" } },
    { key: "instructor_ids", label: "Instructors involved", type: "multi", options: { res: "instructors" }, full: true },
    { key: "max_participants", label: "Max participants (0 = unlimited)", type: "number" }, { key: "waitlist_enabled", label: "Accept waiting list when full", type: "bool" },
    { key: "waitlist_max", label: "Waiting list max", type: "number" }, { key: "admission_requirements", label: "Admission requirements", type: "textarea", full: true }]],
  ["Economics", [
    { key: "fee", label: "Participation fee € (net / taxable)", type: "number" },
    { key: "tax_rate", label: "Tax type applied on the net price", type: "select", options: { static: TAX_OPTIONS } },
    { key: "organizer_id", label: "Organization (company organizing the event – manage in Pages & defaults › Site settings)", type: "select", options: { res: "companies" }, full: true },
    { key: "deposit_amount", label: "Deposit € (balance = total − deposit)", type: "number" },
    { key: "payment_methods", label: "Payment methods (PayPal is offered only when active for the event's company)", type: "multi", options: { static: ["Bank transfer", "PayPal", "Cash", "Card (POS)"] }, full: true },
    { key: "show_payment_methods", label: "Make payment methods public (show the Payment methods section on the event page)", type: "bool", full: true }]],
  ["Charter", [{ key: "charter_ids", label: "Charter offers available for this event", type: "multi", options: { res: "charters", label: "model" }, full: true }]],
];

const RACE_FIELDS = [
  { key: "name", label: "Race name", full: true }, { key: "start_date", label: "Race start date", type: "date" }, { key: "end_date", label: "Race end date", type: "date" },
  { key: "text", label: "Text", type: "textarea", rows: 4, full: true }, { key: "link", label: "Link (race website / notice of race)", full: true },
];

const Services = ({ f, setF }) => {
  const s = f.services || [];
  const upd = (i, k, v) => setF({ ...f, services: s.map((x, j) => (j === i ? { ...x, [k]: v } : x)) });
  return (
    <div className="space-y-2" data-testid="event-services-editor">
      {s.map((x, i) => (
        <div key={i} className="flex gap-2">
          <Input placeholder="Service name" value={x.name} onChange={(e) => upd(i, "name", e.target.value)} data-testid={`service-name-${i}`} />
          <Input type="number" placeholder="€" className="w-32" value={x.price} onChange={(e) => upd(i, "price", Number(e.target.value))} data-testid={`service-price-${i}`} />
          <select value={String(x.tax_rate ?? f.tax_rate ?? 0)} onChange={(e) => upd(i, "tax_rate", Number(e.target.value))} title="Tax type of this service" className="h-10 w-52 rounded-md border border-slate-300 px-2 text-sm" data-testid={`service-tax-${i}`}>
            {TAX_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button onClick={() => setF({ ...f, services: s.filter((_, j) => j !== i) })} className="px-2 text-slate-400 hover:text-rose-500"><Trash2 size={16} /></button>
        </div>
      ))}
      <button onClick={() => setF({ ...f, services: [...s, { name: "", price: 0, tax_rate: Number(f.tax_rate ?? 0) }] })} className="flex items-center gap-1 text-sm font-semibold text-[#00A8B5]" data-testid="add-service-btn"><Plus size={16} />Add service</button>
    </div>
  );
};

const Box = ({ title, children, right }) => (
  <section className="rounded-2xl border border-slate-200 bg-white p-6"><div className="mb-5 flex items-center justify-between"><h2 className="font-display text-lg font-bold text-[#0A2540]">{title}</h2>{right}</div>{children}</section>
);

export default function EventForm() {
  const { id } = useParams();
  const isNew = id === "new";
  const nav = useNavigate();
  const [f, setF] = useState(null);
  const [defaults, setDefaults] = useState({});
  const { data: types, reload: reloadTypes } = useFetch("/admin/event_types");
  const [newType, setNewType] = useState("");
  const [typeOpen, setTypeOpen] = useState(false);
  const addType = async (e) => {
    e.preventDefault();
    const name = newType.trim();
    if ((types || []).some((x) => x.name.toLowerCase() === name.toLowerCase())) return toast.error("This type already exists");
    try {
      await api.post("/admin/event_types", { name, order: (types || []).length + 1, translations: {} });
      setNewType("");
      setTypeOpen(false);
      reloadTypes();
      setF((p) => ({ ...p, type: name }));
      toast.success("Event type added");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  useEffect(() => {
    Promise.all([api.get("/admin/content"), api.get("/admin/companies")]).then(([r, cs]) => {
      const d = r.data.find((c) => c.key === "event_defaults")?.fields || {};
      setDefaults(d);
      const dc = cs.data.find((c) => c.is_default);
      if (isNew) setF({ status: "Draft", type: "Clinic", tax_rate: 7, services: [], charter_ids: [], instructor_ids: [], payment_methods: ["Bank transfer", "PayPal"], sections: { ...d }, ...(dc ? { organizer_id: dc.id } : {}) });
    });
    if (!isNew) api.get(`/admin/events/${id}`).then((r) => setF(r.data));
  }, [id, isNew]);

  if (!f) return null;
  const save = async () => {
    try {
      const { data } = isNew ? await api.post("/admin/events", f) : await api.put(`/admin/events/${id}`, f);
      toast.success("Event saved");
      if (isNew) nav(`/admin/events/${data.id}`);
      else setF(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const translate = async () => {
    const t = toast.loading("Translating with AI…");
    try {
      await api.post(`/admin/translate/events/${id}`);
      toast.success("IT/ES generated. Review in Translations.", { id: t });
    } catch (e) {
      toast.error(errMsg(e), { id: t });
    }
  };
  const setSection = (k, v) => setF({ ...f, sections: { ...(f.sections || {}), [k]: v } });

  return (
    <div className="space-y-6" data-testid="event-form">
      <AdminH title={isNew ? "New event" : f.title} sub={!isNew && f.capacity ? `${f.capacity.registered} registered · ${f.capacity.waitlist} on waiting list` : ""}>
        {!isNew && <button onClick={translate} className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold" data-testid="event-translate-button"><Languages size={16} />AI translate</button>}
        <button onClick={save} className="btn-buoy !py-2.5" data-testid="event-save-button">Save</button>
      </AdminH>
      <Box title="Publication">
        <div className="flex flex-wrap gap-2" data-testid="event-status-picker">
          {(f.status === "Published" ? ["Published", ...STATUSES] : STATUSES).map((s) => <button key={s} onClick={() => setF({ ...f, status: s })} data-testid={`event-status-${s.replace(/ /g, "-").toLowerCase()}`}
            className={`rounded-full border px-4 py-2 text-sm font-semibold ${f.status === s ? "border-[#0A2540] bg-[#0A2540] text-white" : "border-slate-200 text-slate-600"}`}>{s}</button>)}
          <span className="mx-2 h-6 w-px bg-slate-200" />
          <button onClick={() => setF({ ...f, hidden: !f.hidden })} data-testid="event-hide-toggle" aria-pressed={!!f.hidden}
            className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-semibold ${f.hidden ? "border-rose-500 bg-rose-500 text-white" : "border-slate-200 text-slate-600"}`}><EyeOff size={14} />{f.hidden ? "Hidden" : "Hide"}</button>
        </div>
        {f.hidden && <p className="mt-3 text-xs text-rose-600" data-testid="event-hidden-note">This event is hidden from the public event lists (home and Clinics). Its page stays reachable only with the direct link.</p>}
      </Box>
      <Box title="Event type">
        <div className="flex flex-wrap items-center gap-2" data-testid="event-type-picker">
          {(types || []).map((x) => <button key={x.id} onClick={() => setF({ ...f, type: x.name })} data-testid={`event-type-${x.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
            className={`rounded-full border px-4 py-2 text-sm font-semibold ${f.type === x.name ? "border-[#FF5722] bg-[#FF5722] text-white" : "border-slate-200 text-slate-600"}`}>{x.name}</button>)}
          <span className="mx-1 h-6 w-px bg-slate-200" />
          <button type="button" onClick={() => setF({ ...f, by_invitation: !f.by_invitation })} aria-pressed={!!f.by_invitation} data-testid="event-by-invitation-toggle"
            className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-semibold ${f.by_invitation ? "border-[#FFC107] bg-[#FFC107] text-[#0A2540]" : "border-slate-200 text-slate-600"}`}><Star size={14} />By invitation</button>
          <button type="button" onClick={() => setTypeOpen(true)} className="inline-flex items-center gap-1 rounded-full border-2 border-dashed border-[#00A8B5] px-4 py-2 text-sm font-semibold text-[#00A8B5]" data-testid="new-event-type-button"><Plus size={14} />New type</button>
        </div>
        {f.by_invitation && <p className="mt-3 text-xs text-amber-700" data-testid="event-by-invitation-note">Visible only to invited users (after login). Invite guests with the yellow "Invite" button on this event in Admin › Events or on its card in the public Clinics & Events page.
          {(!["Published", "Registrations open", "Registrations closed", "Completed"].includes(f.status) || f.hidden) && <b className="block text-rose-600" data-testid="event-by-invitation-not-public">This event is {f.hidden ? "hidden" : `"${f.status}"`}: it does not appear on the public Clinics & Events page (not even for the admin) until it is published and not hidden. You can still invite from Admin › Events.</b>}</p>}
        <Sheet open={typeOpen} onOpenChange={setTypeOpen}>
          <SheetContent side="right" className="w-full sm:max-w-md" data-testid="new-event-type-sheet">
            <SheetHeader><SheetTitle className="font-display text-2xl">New event type</SheetTitle></SheetHeader>
            <form onSubmit={addType} className="mt-6 space-y-4">
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">Name
                <Input autoFocus placeholder="e.g. Training camp" value={newType} onChange={(e) => setNewType(e.target.value)} className="mt-1.5 h-11 font-normal normal-case tracking-normal" data-testid="new-event-type-input" /></label>
              <p className="text-xs text-slate-500">The new type is selected for this event. The Race section is available only for "Clinic & Race".</p>
              <button disabled={!newType.trim()} className="btn-buoy w-full justify-center disabled:opacity-40" data-testid="new-event-type-add">Create type</button>
            </form>
          </SheetContent>
        </Sheet>
      </Box>
      {f.type === "Clinic & Race" && (
        <Box title="Race" right={<span className="text-xs text-slate-500">Shown on the event page for Clinic & Race types</span>}>
          <div data-testid="event-race-section"><FormGrid prefix="race-" fields={RACE_FIELDS} f={f.race || {}} setF={(upd) => setF((p) => ({ ...p, race: typeof upd === "function" ? upd(p.race || {}) : upd }))} /></div>
        </Box>
      )}
      {GROUPS.map(([title, fields]) => <Box key={title} title={title}><FormGrid fields={fields} f={f} setF={setF} /></Box>)}
      <Box title="Additional services"><Services f={f} setF={setF} /></Box>
      <Box title="Common sections" right={<span className="text-xs text-slate-500">Pre-filled with the standard texts (Pages & defaults)</span>}>
        <div className="grid gap-4 md:grid-cols-2">
          {Object.entries(SECTION_LABELS).map(([k, l]) => (
            <div key={k} className="space-y-1.5">
              <div className="flex items-center justify-between"><p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{l}</p>
                <button onClick={() => setSection(k, defaults[k] || "")} className="flex items-center gap-1 text-xs text-[#00A8B5]" data-testid={`section-reset-${k}`}><RotateCcw size={12} />Standard text</button></div>
              <textarea rows={4} value={f.sections?.[k] || ""} onChange={(e) => setSection(k, e.target.value)} className="w-full rounded-md border border-slate-200 p-3 text-sm" data-testid={`section-${k}-input`} />
            </div>
          ))}
        </div>
      </Box>
      <button onClick={save} className="btn-buoy" data-testid="event-save-button-bottom">Save event</button>
    </div>
  );
}
